import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useRef, useState, forwardRef, useCallback } from 'react';

import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { alpha, useTheme } from '@mui/material/styles';

import { getRatio } from './utils';
import { ImageProps } from './types';
import { useSkeletonVisibility } from './use-skeleton-visibility';

// ----------------------------------------------------------------------

const Image = forwardRef<HTMLSpanElement, ImageProps>(
  (
    {
      ratio,
      overlay,
      disabledEffect = false,
      showLoadingSkeleton = false,
      pauseSkeletonWhenOffscreen = false,
      fadeInDuration = 400,
      //
      alt,
      src,
      onLoad,
      onError,
      afterLoad,
      delayTime,
      threshold,
      beforeLoad,
      delayMethod,
      placeholder,
      wrapperProps,
      scrollPosition,
      effect = 'blur',
      visibleByDefault,
      wrapperClassName,
      useIntersectionObserver,
      sx,
      ...other
    },
    ref
  ) => {
    const theme = useTheme();
    const imageRef = useRef<HTMLSpanElement | null>(null);
    const [loadedSrc, setLoadedSrc] = useState<string | undefined>();
    const [loadingSrc, setLoadingSrc] = useState<string | undefined>();
    const [failedSrc, setFailedSrc] = useState<string | undefined>();
    const isLoaded = Boolean(src) && loadedSrc === src;
    const hasError = Boolean(src) && failedSrc === src;
    const isLoading = Boolean(src) && loadingSrc === src && !isLoaded && !hasError;
    const isVisible = useSkeletonVisibility(
      imageRef,
      showLoadingSkeleton && pauseSkeletonWhenOffscreen && isLoading
    );

    const handleRef = useCallback(
      (element: HTMLSpanElement | null) => {
        imageRef.current = element;
        if (typeof ref === 'function') {
          ref(element);
        } else if (ref) {
          ref.current = element;
        }
      },
      [ref]
    );

    const handleBeforeLoad = () => {
      if (showLoadingSkeleton) {
        setLoadingSrc(src);
        setFailedSrc(undefined);
        setLoadedSrc(undefined);
      }
      beforeLoad?.();
    };

    const handleLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
      setLoadedSrc(src);
      onLoad?.(event);
    };

    const handleError = (event: React.SyntheticEvent<HTMLImageElement>) => {
      if (showLoadingSkeleton) {
        setFailedSrc(src);
      }
      onError?.(event);
    };

    const overlayStyles = !!overlay && {
      '&:before': {
        content: "''",
        top: 0,
        left: 0,
        width: 1,
        height: 1,
        zIndex: 1,
        position: 'absolute',
        background: overlay || alpha(theme.palette.grey[900], 0.48),
      },
    };

    const content =
      hasError && showLoadingSkeleton ? (
        <Box
          component="img"
          src="/assets/placeholder.svg"
          alt={alt}
          sx={{
            width: 1,
            height: 1,
            objectFit: 'cover',
            verticalAlign: 'bottom',
            ...(!!ratio && { top: 0, left: 0, position: 'absolute' }),
          }}
        />
      ) : (
        <Box
          key={src}
          component={LazyLoadImage}
          //
          alt={alt}
          src={src}
          onLoad={handleLoad}
          onError={handleError}
          afterLoad={afterLoad}
          delayTime={delayTime}
          threshold={threshold}
          beforeLoad={handleBeforeLoad}
          delayMethod={delayMethod}
          placeholder={placeholder}
          wrapperProps={wrapperProps}
          scrollPosition={scrollPosition}
          visibleByDefault={visibleByDefault}
          effect={disabledEffect || showLoadingSkeleton ? undefined : effect}
          useIntersectionObserver={useIntersectionObserver}
          wrapperClassName={wrapperClassName || 'component-image-wrapper'}
          placeholderSrc={
            disabledEffect || showLoadingSkeleton
              ? '/assets/transparent.png'
              : '/assets/placeholder.svg'
          }
          //
          sx={{
            width: 1,
            height: 1,
            objectFit: 'cover',
            verticalAlign: 'bottom',
            ...(!!ratio && {
              top: 0,
              left: 0,
              position: 'absolute',
            }),
          }}
        />
      );

    return (
      <Box
        ref={handleRef}
        component="span"
        className="component-image"
        sx={{
          overflow: 'hidden',
          position: 'relative',
          verticalAlign: 'bottom',
          display: 'inline-block',
          ...(!!ratio && {
            width: 1,
          }),
          '& span.component-image-wrapper': {
            width: 1,
            height: 1,
            verticalAlign: 'bottom',
            backgroundSize: 'cover !important',
            ...(!!ratio && {
              pt: getRatio(ratio),
            }),
          },
          ...(showLoadingSkeleton && {
            '& img': {
              opacity: isLoaded || hasError ? 1 : 0,
              transition: `opacity ${fadeInDuration}ms ease-out`,
            },
          }),
          ...overlayStyles,
          ...sx,
        }}
        {...other}
      >
        {showLoadingSkeleton && (
          <Skeleton
            animation={
              isLoaded || hasError || !src || (pauseSkeletonWhenOffscreen && !isLoading)
                ? false
                : 'pulse'
            }
            variant="rectangular"
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 1,
              width: '100%',
              height: '100%',
              animationPlayState: pauseSkeletonWhenOffscreen && !isVisible ? 'paused' : 'running',
              opacity: isLoaded || hasError ? 0 : 1,
              transition: `opacity ${fadeInDuration}ms ease-out`,
              pointerEvents: 'none',
            }}
          />
        )}
        {content}
      </Box>
    );
  }
);

export default Image;
